import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Component
* SubType: ProtectedComponent
* Reason: Per-contract "Vertragsabrechnung" — a chronological, server-paged table of the CALCULATED
*         commission records (Soll) booked against ONE contract, with AP/BP/Sonstige provision-type badges
*         and full-contract AP/BP/Sonstige/Gesamt summary tiles fed from a dedicated summary endpoint.
*         Read-only display component, embedded into the CM contract page via the @Input() contractId.
*/

import {CnCalculationApiService} from '../../../service/api/cn-calculation/cn-calculation-api.service';
import {CnContractCalculationDto} from '../../../model/dto/api/cn-contract-calculation.dto';
import {CnTagSeverity} from '../../../shared/cn-status-color.helper';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
* Provision-type bucket used by the badge. Derived from the GDV classification code the backend resolves
* onto every calculation record:
*   GDV 1 → AP  (Abschlussprovision, CommissionProvisionType.ACQUISITION)
*   GDV 7 → BP  (Bestandspflegeprovision, CommissionProvisionType.PORTFOLIO_CARE)
*   else  → SONSTIGE (every other / missing GDV code)
*/
type ProvisionBucket = 'AP' | 'BP' | 'SONSTIGE';

/** Visual descriptor for a provision-type badge (label + theme-token colors, no HEX/RGB). */
interface ProvisionBadge {
    /** Short German badge label shown in the cell ("AP" / "BP" / "Sonstige"). */
    label: string;
    /** Inline theme-token style for the badge (background + text), kept consistent with cn status tags. */
    style: { [style: string]: string };
}

/**
* Per-contract "Vertragsabrechnung".
*
* A chronological view of every CALCULATED commission record (Soll) booked against ONE contract
* ({@link contractId}), oldest booking month first. Each row carries its resolved provision type as a
* colored badge — blue "AP" (GDV 1 / ACQUISITION), pink "BP" (GDV 7 / PORTFOLIO_CARE) or a neutral
* "Sonstige" — and its calculation lifecycle status as a German Klartext label.
*
* <p><b>Server-side paging:</b> the table is a PrimeNG lazy table — it loads ONE page at a time via
* {@link CnCalculationApiService#getCalculations} (GET /cn/contract/{id}/calculations). The backend
* already orders the rows oldest booking month first, so no client-side sort is applied.</p>
*
* <p><b>Summary tiles:</b> the AP / BP / Sonstige / Gesamt sums are FULL-CONTRACT totals loaded from a
* dedicated aggregate endpoint ({@link CnCalculationApiService#getContractSummary},
* GET /cn/contract/{id}/calculations/summary), independent of the currently paged rows. The summary is
* fetched in parallel with the first page whenever {@link contractId} is (re)set.</p>
*
* <p>UI states: loading / ready / empty / error are handled explicitly, consistent with the cn UX.</p>
*
* Reusable in isolation — a host (the CM contract page) only has to bind {@link contractId}.
*/
@Component({
    selector: 'mvs-cn-contract-commission',
    templateUrl: './cn-contract-commission.component.html',
    styleUrls: ['./cn-contract-commission.component.scss'],
    standalone: false
})
export class CnContractCommissionComponent implements OnInit, OnChanges {

    /** Default page size for the lazy table (matches the API default). */
    static readonly DEFAULT_ROWS = 50;

    /** GDV classification code for an Abschlussprovision (AP). */
    static readonly GDV_AP = 1;
    /** GDV classification code for a Bestandspflegeprovision (BP). */
    static readonly GDV_BP = 7;

    /**
     * The contract whose Vertragsabrechnung to display. Setting / changing it (re)loads page 0 and the
     * full-contract summary. A null / undefined value clears the view (nothing is loaded until a real id
     * arrives from the host).
     */
    @Input() contractId?: number | null;

    // ----- Row data (ONE server page at a time) -----
    rows: CnContractCalculationDto[] = [];
    listLoading: boolean = false;
    listError: string | null = null;

    // ----- Server-side pagination (lazy) -----
    /** 0-based offset of the current page (PrimeNG lazy `first`). */
    first: number = 0;
    rowsPerPage: number = CnContractCommissionComponent.DEFAULT_ROWS;
    /** Total rows for this contract across all pages (drives `[totalRecords]`). */
    totalRecords: number = 0;

    // ----- Summary tiles (full-contract totals from the aggregate endpoint) -----
    /** Total calculated AP (GDV 1) commission across the whole contract. */
    sumAp: number = 0;
    /** Total calculated BP (GDV 7) commission across the whole contract. */
    sumBp: number = 0;
    /** Total calculated Sonstige commission across the whole contract. */
    sumSonstige: number = 0;
    /** Grand total calculated commission across the whole contract (AP + BP + Sonstige). */
    sumGesamt: number = 0;

    initialized: boolean = false;

    constructor(private calculationApiService: CnCalculationApiService) {
    }

    ngOnInit(): void {
        this.initialized = true;
        // The lazy p-table fires its first (onLazyLoad) on init, which loads page 0 (when a contractId is
        // already bound). The full-contract summary is loaded here in parallel for that initial contractId.
        this.loadSummary();
    }

    /**
    * React to a changed contract: reset to page 0, reload the page and reload the full-contract summary.
    * Skipped before the first init so the lazy table's own initial (onLazyLoad) is the single entry point
    * for the first page load.
    */
    ngOnChanges(changes: SimpleChanges): void {
        if (this.initialized && changes['contractId']) {
            this.first = 0;
            this.loadPage();
            this.loadSummary();
        }
    }

    // =====================================================================================
    // Data loading
    // =====================================================================================

    /** 0-based page index derived from the lazy `first` offset + the current page size. */
    private get currentPageIndex(): number {
        return this.rowsPerPage > 0 ? Math.floor(this.first / this.rowsPerPage) : 0;
    }

    /**
    * Load ONE server page of calculation records for the bound contract. Without a contractId there is
    * nothing to load — the table resolves straight to its empty state. The backend orders oldest booking
    * month first, so the rows are used as-is (no client sort).
    */
    loadPage(): void {
        if (this.contractId === undefined || this.contractId === null) {
            this.rows = [];
            this.totalRecords = 0;
            this.listLoading = false;
            this.listError = null;
            return;
        }

        this.listLoading = true;
        this.listError = null;

        this.calculationApiService
            .getCalculations(this.contractId, this.currentPageIndex, this.rowsPerPage)
            .subscribe({
                next: (pageDto) => {
                    this.rows = pageDto?.content ?? [];
                    this.totalRecords = pageDto?.totalElements ?? this.rows.length;
                    this.listLoading = false;
                },
                error: (err) => {
                    this.rows = [];
                    this.totalRecords = 0;
                    this.listLoading = false;
                    this.listError = cnErrorMessage(err);
                }
            });
    }

    /**
    * Load the FULL-CONTRACT AP / BP / Sonstige / Gesamt totals from the dedicated summary endpoint. These
    * tiles are independent of paging, so this is fired once per contractId (alongside the first page). A
    * missing contractId clears the tiles; a summary error is swallowed (the tiles fall back to zero) so it
    * never masks the table's own error surface.
    */
    loadSummary(): void {
        if (this.contractId === undefined || this.contractId === null) {
            this.resetSummary();
            return;
        }

        this.calculationApiService
            .getContractSummary(this.contractId)
            .subscribe({
                next: (totals) => {
                    this.sumAp = totals?.ap?.total ?? 0;
                    this.sumBp = totals?.bp?.total ?? 0;
                    this.sumSonstige = totals?.sonstige?.total ?? 0;
                    this.sumGesamt = totals?.gesamt?.total ?? 0;
                },
                error: () => {
                    this.resetSummary();
                }
            });
    }

    /**
    * PrimeNG lazy-load callback ([lazy]=true / (onLazyLoad)). Translates the table's first / rows into a
    * server page request. The backend owns the (oldest-first) ordering, so no sort field is forwarded.
    * PrimeNG types `first` / `rows` as optional, so they are narrowed defensively here.
    */
    onLazyLoad(event: { first?: number; rows?: number | null }): void {
        this.first = event.first ?? 0;
        this.rowsPerPage = event.rows ?? this.rowsPerPage;
        this.loadPage();
    }

    /**
    * Manual refresh affordance: reload the current page AND the full-contract summary (keeps the paging
    * position).
    */
    manualRefresh(): void {
        this.loadPage();
        this.loadSummary();
    }

    // =====================================================================================
    // Summary tiles
    // =====================================================================================

    /** Reset every summary tile to zero (used on clear / error). */
    private resetSummary(): void {
        this.sumAp = 0;
        this.sumBp = 0;
        this.sumSonstige = 0;
        this.sumGesamt = 0;
    }

    // =====================================================================================
    // Provision-type bucket + badge
    // =====================================================================================

    /**
    * Bucket a calculation record by its resolved GDV provision-type code: GDV 1 → AP, GDV 7 → BP,
    * everything else (including a missing code) → SONSTIGE.
    */
    provisionBucket(row: CnContractCalculationDto): ProvisionBucket {
        switch (row?.provisionTypeGdvCode) {
            case CnContractCommissionComponent.GDV_AP:
                return 'AP';
            case CnContractCommissionComponent.GDV_BP:
                return 'BP';
            default:
                return 'SONSTIGE';
        }
    }

    /**
    * The provision-type badge for a row: blue "AP", pink "BP" or a neutral grey "Sonstige". Only theme
    * color tokens are used (var(--*-200) background / var(--*-800) text), mirroring how the cn status
    * helper styles its hand-built tags — no HEX/RGB and no hardcoded business color.
    */
    provisionBadge(row: CnContractCalculationDto): ProvisionBadge {
        switch (this.provisionBucket(row)) {
            case 'AP':
                return {label: 'AP', style: {'background-color': 'var(--blue-200)', color: 'var(--blue-800)'}};
            case 'BP':
                return {label: 'BP', style: {'background-color': 'var(--pink-200)', color: 'var(--pink-800)'}};
            default:
                return {
                    label: 'Sonstige',
                    style: {'background-color': 'var(--surface-300)', color: 'var(--surface-800)'}
                };
        }
    }

    /**
    * Long-form German tooltip for a provision-type badge, naming the provision art behind the GDV code.
    */
    provisionTooltip(row: CnContractCalculationDto): string {
        switch (this.provisionBucket(row)) {
            case 'AP':
                return 'Abschlussprovision (GDV 1)';
            case 'BP':
                return 'Bestandspflegeprovision (GDV 7)';
            default:
                return 'Sonstige Provisionsart';
        }
    }

    // =====================================================================================
    // Row helpers (Klartext labels, status)
    // =====================================================================================

    /**
    * Model display label, falling back to "#"+commissionModelId when the backend could not resolve a name.
    */
    modelLabel(row: CnContractCalculationDto): string {
        if (row?.modelName && row.modelName.trim().length > 0) {
            return row.modelName;
        }
        return row?.commissionModelId !== undefined && row.commissionModelId !== null
            ? '#' + row.commissionModelId
            : '–';
    }

    /**
    * The PrimeNG `p-tag` severity for a calculation status (success for an active record, secondary for a
    * superseded one).
    */
    calculationStatusSeverity(status: string | null | undefined): CnTagSeverity {
        return status === 'SUPERSEDED' ? 'secondary' : 'success';
    }

    /**
    * German Klartext label for a calculation lifecycle status: ACTIVE → "Aktiv", SUPERSEDED → "Ersetzt".
    */
    calculationStatusLabel(status: string | null | undefined): string {
        switch (status) {
            case 'ACTIVE':
                return 'Aktiv';
            case 'SUPERSEDED':
                return 'Ersetzt';
            default:
                return status && status.trim().length > 0 ? status : '–';
        }
    }
}
